/**
 * CERTIFICATE UPLOADER SERVICE
 * =============================
 * Servicio para subida segura de certificados usando Web Crypto API
 *
 * Flujo de seguridad:
 * 1. Solicitar challenge al servidor
 * 2. Validar certificado localmente (fecha, formato)
 * 3. Cifrar certificado con AES-256-GCM (clave aleatoria)
 * 4. Cifrar clave simétrica con RSA del servidor
 * 5. Enviar todo cifrado
 *
 * El certificado NUNCA se envía en claro al servidor
 */

const API_URL = process.env.REACT_APP_API_URL || '';

/**
 * Clase principal del uploader de certificados
 */
class CertificateUploader {
  constructor() {
    this.challenge = null;
    this.serverPublicKey = null;
    this.expiresAt = null;
  }

  /**
   * Headers con autenticación (usa cookies httpOnly)
   */
  getHeaders(contentType = 'application/json') {
    const headers = {};
    if (contentType) {
      headers['Content-Type'] = contentType;
    }
    return headers;
  }

  /**
   * Paso 1: Solicitar challenge al servidor
   */
  async requestChallenge() {
    const response = await fetch(`${API_URL}/api/empresa/certificados/challenge`, {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Error al solicitar challenge');
    }

    const data = await response.json();

    this.challenge = data.challenge;
    this.serverPublicKey = data.serverPublicKey;
    this.expiresAt = Date.now() + (data.expiresIn * 1000);

    return {
      challenge: this.challenge,
      expiresIn: data.expiresIn
    };
  }

  /**
   * Verificar si el challenge sigue válido
   */
  isChallengeValid() {
    return this.challenge && this.expiresAt && Date.now() < this.expiresAt;
  }

  /**
   * Paso 2: Validar certificado localmente
   *
   * @param {File} file - Archivo .p12 o .pfx
   * @param {string} password - Contraseña del certificado
   * @returns {Object} - Metadata básica del certificado
   */
  async validateLocally(file, password) {
    // Validar extensión
    const validExtensions = ['.p12', '.pfx'];
    const extension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));

    if (!validExtensions.includes(extension)) {
      throw new Error('El archivo debe ser .p12 o .pfx');
    }

    // Validar tamaño (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      throw new Error('El archivo es demasiado grande (máximo 5MB)');
    }

    // Leer archivo
    const buffer = await this.readFileAsBuffer(file);

    // Intentar parsear con la librería forge (si está disponible)
    // Por seguridad, el parsing real se hace en el servidor
    // Aquí solo hacemos validaciones básicas

    return {
      filename: file.name,
      size: file.size,
      buffer: buffer
    };
  }

  /**
   * Leer archivo como ArrayBuffer
   */
  readFileAsBuffer(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(new Uint8Array(reader.result));
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Paso 3: Cifrar datos con AES-256-GCM
   *
   * @param {Uint8Array} data - Datos a cifrar
   * @returns {Object} - { encryptedData, iv, authTag, symmetricKey }
   */
  async encryptWithAES(data) {
    // Generar clave simétrica aleatoria
    const symmetricKey = await crypto.subtle.generateKey(
      {
        name: 'AES-GCM',
        length: 256
      },
      true, // extractable
      ['encrypt']
    );

    // Generar IV aleatorio
    const iv = crypto.getRandomValues(new Uint8Array(12));

    // Cifrar datos
    const encryptedBuffer = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv,
        tagLength: 128
      },
      symmetricKey,
      data
    );

    // En AES-GCM, el auth tag está al final del resultado
    const encryptedArray = new Uint8Array(encryptedBuffer);
    const authTag = encryptedArray.slice(-16);
    const encryptedData = encryptedArray.slice(0, -16);

    // Exportar clave para cifrarla con RSA
    const rawKey = await crypto.subtle.exportKey('raw', symmetricKey);

    return {
      encryptedData: this.arrayBufferToBase64(encryptedData),
      iv: this.arrayBufferToBase64(iv),
      authTag: this.arrayBufferToBase64(authTag),
      symmetricKey: new Uint8Array(rawKey)
    };
  }

  /**
   * Paso 4: Cifrar clave simétrica con RSA del servidor
   *
   * @param {Uint8Array} symmetricKey - Clave simétrica a cifrar
   * @returns {string} - Clave cifrada en base64
   */
  async encryptKeyWithRSA(symmetricKey) {
    if (!this.serverPublicKey) {
      throw new Error('No hay clave pública del servidor. Solicite un challenge primero.');
    }

    // Importar clave pública PEM
    const publicKey = await this.importRSAPublicKey(this.serverPublicKey);

    // Cifrar clave simétrica
    const encryptedKey = await crypto.subtle.encrypt(
      {
        name: 'RSA-OAEP'
      },
      publicKey,
      symmetricKey
    );

    return this.arrayBufferToBase64(encryptedKey);
  }

  /**
   * Importar clave pública RSA desde PEM
   */
  async importRSAPublicKey(pem) {
    // Extraer base64 del PEM
    const pemContents = pem
      .replace('-----BEGIN PUBLIC KEY-----', '')
      .replace('-----END PUBLIC KEY-----', '')
      .replace(/\s/g, '');

    const binaryDer = this.base64ToArrayBuffer(pemContents);

    return await crypto.subtle.importKey(
      'spki',
      binaryDer,
      {
        name: 'RSA-OAEP',
        hash: 'SHA-256'
      },
      false,
      ['encrypt']
    );
  }

  /**
   * Paso 5: Subir certificado cifrado
   *
   * @param {File} file - Archivo del certificado
   * @param {string} password - Contraseña del certificado
   * @param {Function} onProgress - Callback de progreso
   * @returns {Object} - Resultado del servidor
   */
  async upload(file, password, onProgress = () => {}) {
    // 1. Solicitar challenge si no hay uno válido
    if (!this.isChallengeValid()) {
      onProgress({ step: 1, message: 'Solicitando challenge seguro...' });
      await this.requestChallenge();
    }

    // 2. Validar localmente
    onProgress({ step: 2, message: 'Validando archivo...' });
    const { buffer } = await this.validateLocally(file, password);

    // 3. Cifrar certificado
    onProgress({ step: 3, message: 'Cifrando certificado...' });
    const encryptedCert = await this.encryptWithAES(buffer);

    // 4. Cifrar password
    onProgress({ step: 4, message: 'Cifrando contraseña...' });
    const passwordBuffer = new TextEncoder().encode(password);
    const encryptedPassword = await this.encryptWithAES(passwordBuffer);

    // 5. Cifrar claves simétricas con RSA del servidor
    onProgress({ step: 5, message: 'Protegiendo claves...' });
    const encryptedKey = await this.encryptKeyWithRSA(encryptedCert.symmetricKey);

    // 6. Enviar al servidor
    onProgress({ step: 6, message: 'Enviando al servidor...' });

    const body = {
      certificado_cifrado: encryptedCert.encryptedData,
      iv: encryptedCert.iv,
      auth_tag: encryptedCert.authTag,
      clave_cifrada: encryptedKey,
      password_cifrado: encryptedPassword.encryptedData,
      password_iv: encryptedPassword.iv,
      password_auth_tag: encryptedPassword.authTag,
      challenge: this.challenge
    };

    const response = await fetch(`${API_URL}/api/empresa/certificados/upload`, {
      method: 'POST',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify(body)
    });

    // Limpiar datos sensibles
    this.clearSensitiveData(encryptedCert, encryptedPassword);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Error al subir certificado');
    }

    onProgress({ step: 7, message: 'Certificado subido correctamente' });

    // Invalidar challenge usado
    this.challenge = null;

    return await response.json();
  }

  /**
   * Obtener estado del certificado
   */
  async getStatus() {
    const response = await fetch(`${API_URL}/api/empresa/certificados/status`, {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Error al obtener estado');
    }

    return await response.json();
  }

  /**
   * Obtener logs de auditoría
   */
  async getLogs(options = {}) {
    const params = new URLSearchParams();
    if (options.tipo) params.append('tipo', options.tipo);
    if (options.nivel) params.append('nivel', options.nivel);
    if (options.desde) params.append('desde', options.desde);
    if (options.hasta) params.append('hasta', options.hasta);
    if (options.limite) params.append('limite', options.limite);
    if (options.pagina) params.append('pagina', options.pagina);

    const response = await fetch(`${API_URL}/api/empresa/certificados/logs?${params}`, {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Error al obtener logs');
    }

    return await response.json();
  }

  /**
   * Eliminar certificado
   */
  async deleteCertificate(motivo) {
    const response = await fetch(`${API_URL}/api/empresa/certificados`, {
      method: 'DELETE',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ motivo })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Error al eliminar certificado');
    }

    return await response.json();
  }

  /**
   * Verificar integridad del audit trail
   */
  async verifyIntegrity() {
    const response = await fetch(`${API_URL}/api/empresa/certificados/integrity`, {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Error al verificar integridad');
    }

    return await response.json();
  }

  // ========================================
  // UTILIDADES
  // ========================================

  /**
   * Convertir ArrayBuffer a Base64
   */
  arrayBufferToBase64(buffer) {
    const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /**
   * Convertir Base64 a ArrayBuffer
   */
  base64ToArrayBuffer(base64) {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }

  /**
   * Limpiar datos sensibles de memoria
   */
  clearSensitiveData(...objects) {
    for (const obj of objects) {
      if (obj && obj.symmetricKey) {
        obj.symmetricKey.fill(0);
      }
    }
  }
}

// Instancia singleton
const certificateUploader = new CertificateUploader();

export default certificateUploader;
export { CertificateUploader };
